'use client'

import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import type { UserProfile } from '@shared/types/domain-types'
import { createAuthClient } from '@/lib/auth/client'
import { getCurrentSeasonId } from '@/lib/config/governance'
import { getThemeById } from '@/lib/themes'
import { queryClient } from '@/lib/query/query-client'
import { isShellSessionQueryKey } from '@/lib/query/query-keys'
import { readShellCacheSnapshot, seedShellCachesFromBootstrap } from '@/lib/query/shell-cache'
import { DEFAULT_STATE, type AppSessionState } from './types'
import { mapNotificationsFromApi } from './mappers'
import { normalizeSessionState, normalizeEnabledModules, normalizeNotificationPreferences, normalizeAccessibility } from './normalizers'
import {
  SessionContext,
  AccessibilityContext,
  NotificationPreferencesContext,
  UserProfileContext,
  UserContext,
  PreferencesContext,
  SessionMetaContext,
  setGlobalSessionUpdater,
  type PreferencesSlice,
  type SessionMetaSlice,
} from './context'
import { sessionStore } from './session-store'
import { api } from '@/lib/api'
import type { ApiShellBootstrapResponse } from '@/lib/api/types'

export function AppSessionProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [authClient] = useState(() => createAuthClient())
  const [session, setSession] = useState<AppSessionState>(DEFAULT_STATE)

  const updateAppSessionState = useCallback((updater: (state: AppSessionState) => AppSessionState) => {
    setSession((prev) => updater(prev))
  }, [])

  // HAB-142: mirror session state into the external selector store
  // (session-store.ts) so `useSessionSelector` consumers subscribe to derived
  // values and skip the re-render cascade caused by the fresh slice object
  // references normalizeSessionState produces on every update. Runs before paint;
  // the store is a read replica — all writes still flow through setSession /
  // updateAppSessionState.
  useLayoutEffect(() => {
    sessionStore.setState(session)
  }, [session])

  useEffect(() => {
    setGlobalSessionUpdater(updateAppSessionState)
    return () => {
      setGlobalSessionUpdater(null)
    }
  }, [updateAppSessionState])

  useEffect(() => {
    const buildFallbackProfile = (user: any): UserProfile => {
      const metadata = user?.user_metadata ?? {}
      const username = metadata.username ?? user?.email?.split('@')[0] ?? `user_${user.id.slice(0, 8)}`
      const displayName = metadata.display_name ?? username

      return {
        displayName,
        username,
        avatarUrl: metadata.avatar_url ?? '',
        annualLevel: 1,
        annualXp: 0,
        annualXpToNext: 500,
        lifetimeLevel: 1,
        eternalLevel: 1,
        habitCoins: 0,
        hasPremium: false,
        isLifetimePass: false,
        passDaysRemaining: 0,
      }
    }

    const getBootstrapData = async (): Promise<ApiShellBootstrapResponse | null> => {
      try {
        return await api.get<ApiShellBootstrapResponse>('/bootstrap')
      } catch {
        return null
      }
    }

    const init = async () => {
      setSession((prev) => normalizeSessionState({ ...prev, isLoading: true }))

      try {
        const {
          data: { session: authSession },
          error,
        } = await authClient.auth.getUser()

        if (error) {
          queryClient.removeQueries({ queryKey: ['session'] })
          setSession(() => normalizeSessionState({ ...DEFAULT_STATE, hydrated: true, isLoading: false }))
          return
        }

        if (!authSession?.user) {
          queryClient.removeQueries({ queryKey: ['session'] })
          setSession(() => normalizeSessionState({ ...DEFAULT_STATE, hydrated: true, isLoading: false }))
          return
        }

        const metadata = (authSession.user.user_metadata ?? {}) as {
          first_access?: boolean
          onboarding_completed?: boolean
        }

        const metadataFirstAccess = typeof metadata.first_access === 'boolean' ? metadata.first_access : null
        const metadataOnboardingCompleted =
          typeof metadata.onboarding_completed === 'boolean' ? metadata.onboarding_completed : null

        const firstAccess =
          metadataFirstAccess ??
          (metadataOnboardingCompleted === null ? false : !metadataOnboardingCompleted)
        const onboardingCompleted =
          metadataOnboardingCompleted ??
          (metadataFirstAccess === null ? true : !metadataFirstAccess)

        const bootstrap = await getBootstrapData()
        const fallbackProfile = buildFallbackProfile(authSession.user)
        if (bootstrap) {
          seedShellCachesFromBootstrap(queryClient, authSession.user.id, bootstrap)
        }
        const shellSnapshot = readShellCacheSnapshot(queryClient, authSession.user.id)
        const resolvedProfile = shellSnapshot.profile ?? fallbackProfile

        if (!shellSnapshot.profile) {
          console.warn('[Session] Using fallback profile - backend profile unavailable', {
            userId: authSession.user.id,
          })
        }

        setSession((prev) =>
          normalizeSessionState({
            ...prev,
            isAuthenticated: true,
            isLoading: false,
            hydrated: true,
            activeUserId: authSession.user.id,
            user: {
              id: authSession.user.id,
              displayName: resolvedProfile.displayName ?? 'Aventureiro',
              username: resolvedProfile.username ?? `user_${authSession.user.id.slice(0, 8)}`,
              avatarUrl: resolvedProfile.avatarUrl ?? '',
              email: authSession.user.email ?? '',
              firstAccess,
              onboardingCompleted,
              enabledModules: shellSnapshot.preferences?.enabledModules ?? normalizeEnabledModules(undefined),
            },
            userProfile: resolvedProfile,
            firstAccess,
            onboardingCompleted,
            enabledModules: shellSnapshot.preferences?.enabledModules ?? normalizeEnabledModules(undefined),
            accessibility: shellSnapshot.preferences?.accessibility ?? normalizeAccessibility(undefined),
            notificationPreferences:
              shellSnapshot.preferences?.notificationPreferences ?? normalizeNotificationPreferences(undefined),
            notifications: mapNotificationsFromApi(shellSnapshot.notificationsSummary?.items ?? []),
            notificationsUnreadCount: Math.max(0, Number(shellSnapshot.notificationsSummary?.unreadCount ?? 0)),
            notificationsTotal: Math.max(0, Number(shellSnapshot.notificationsSummary?.total ?? 0)),
            notificationsPreviewLimit: Math.max(
              1,
              Number(shellSnapshot.notificationsSummary?.previewLimit ?? DEFAULT_STATE.notificationsPreviewLimit),
            ),
            postOnboardingRedirect: prev.postOnboardingRedirect,
            network: { forceOffline: false, syncQueue: [] },
            cycle: {
              lastSeenYear: new Date().getFullYear(),
              pendingYearReset: null,
              lastSeenSeasonId: getCurrentSeasonId(),
              pendingSeasonResetId: null,
            },
            arsenal: {
              ...prev.arsenal,
              activeTheme: shellSnapshot.preferences?.activeThemeName ?? 'Core Protocol',
              activeThemeId: shellSnapshot.preferences?.activeThemeId ?? 'core-protocol',
            },
            ownedEmporiumItemIds: shellSnapshot.inventorySummary?.ownedEmporiumItemIds ?? [],
          }),
        )
      } catch (err: any) {
        setSession(() => normalizeSessionState({ ...DEFAULT_STATE, hydrated: true, isLoading: false }))
      }
    }

    void init()

    const {
      data: { subscription },
    } = authClient.auth.onAuthStateChange(async (event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
        void init()
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [authClient])

  useEffect(() => {
    if (!session.activeUserId) return

    const shallowEqual = (a: Record<string, unknown> | null, b: Record<string, unknown> | null): boolean => {
      if (a === b) return true
      if (a === null || b === null) return false
      if (typeof a !== 'object' || typeof b !== 'object') return false
      const keysA = Object.keys(a)
      const keysB = Object.keys(b)
      if (keysA.length !== keysB.length) return false
      for (const key of keysA) {
        if (a[key] !== b[key]) return false
      }
      return true
    }

    const previousSnapshotRef = { current: null as ReturnType<typeof readShellCacheSnapshot> | null }

    const syncFromShellCache = () => {
      const snapshot = readShellCacheSnapshot(queryClient, session.activeUserId)
      
      if (previousSnapshotRef.current && shallowEqual(previousSnapshotRef.current, snapshot)) {
        return
      }
      previousSnapshotRef.current = snapshot
      
      setSession((prev) =>
        normalizeSessionState({
          ...prev,
          userProfile: snapshot.profile ?? prev.userProfile,
          enabledModules: snapshot.preferences?.enabledModules ?? prev.enabledModules,
          accessibility: snapshot.preferences?.accessibility ?? prev.accessibility,
          notificationPreferences: snapshot.preferences?.notificationPreferences ?? prev.notificationPreferences,
          notifications: snapshot.notificationsSummary ? mapNotificationsFromApi(snapshot.notificationsSummary.items) : prev.notifications,
          notificationsUnreadCount: snapshot.notificationsSummary?.unreadCount ?? prev.notificationsUnreadCount,
          notificationsTotal: snapshot.notificationsSummary?.total ?? prev.notificationsTotal,
          notificationsPreviewLimit: snapshot.notificationsSummary?.previewLimit ?? prev.notificationsPreviewLimit,
          ownedEmporiumItemIds: snapshot.inventorySummary
            ? snapshot.inventorySummary.ownedEmporiumItemIds
            : prev.ownedEmporiumItemIds,
          arsenal: {
            ...prev.arsenal,
            activeTheme: snapshot.preferences?.activeThemeName ?? prev.arsenal.activeTheme,
            activeThemeId: snapshot.preferences?.activeThemeId ?? prev.arsenal.activeThemeId,
          },
          user: prev.user
            ? {
                ...prev.user,
                displayName: snapshot.profile?.displayName ?? prev.user.displayName,
                username: snapshot.profile?.username ?? prev.user.username,
                avatarUrl: snapshot.profile?.avatarUrl ?? prev.user.avatarUrl,
                enabledModules: snapshot.preferences?.enabledModules ?? prev.user.enabledModules,
              }
            : prev.user,
        }),
      )
    }

    syncFromShellCache()
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (!event?.query || !isShellSessionQueryKey(event.query.queryKey, session.activeUserId)) return
      syncFromShellCache()
    })

    return unsubscribe
  }, [session.activeUserId])

  const accessibility = React.useMemo(() => session.accessibility, [session.accessibility])
  const notificationPreferences = React.useMemo(
    () => session.notificationPreferences,
    [session.notificationPreferences],
  )
  const userProfile = React.useMemo(() => session.userProfile, [session.userProfile])

  // HAB-49: focused slice values, each memoized on the fields it depends on so
  // consumers of `useUser` / `usePreferences` / `useSessionMeta` only re-render
  // when their slice actually changes. The full SessionContext below still
  // changes on every state update — keep migrating consumers to slice hooks.
  const userSliceValue = React.useMemo(() => session.user, [session.user])
  const preferencesSliceValue = React.useMemo<PreferencesSlice>(
    () => ({
      enabledModules: session.enabledModules,
      accessibility: session.accessibility,
      notificationPreferences: session.notificationPreferences,
      activeTheme: session.arsenal.activeTheme,
      activeThemeId: session.arsenal.activeThemeId,
    }),
    [
      session.enabledModules,
      session.accessibility,
      session.notificationPreferences,
      session.arsenal.activeTheme,
      session.arsenal.activeThemeId,
    ],
  )
  const sessionMetaValue = React.useMemo<SessionMetaSlice>(
    () => ({
      hydrated: session.hydrated,
      isLoading: session.isLoading,
      isAuthenticated: session.isAuthenticated,
      activeUserId: session.activeUserId,
      firstAccess: session.firstAccess,
      onboardingCompleted: session.onboardingCompleted,
      postOnboardingRedirect: session.postOnboardingRedirect,
    }),
    [
      session.hydrated,
      session.isLoading,
      session.isAuthenticated,
      session.activeUserId,
      session.firstAccess,
      session.onboardingCompleted,
      session.postOnboardingRedirect,
    ],
  )
  const sessionContextValue = React.useMemo(
    () => ({ session, updateAppSessionState }),
    [session, updateAppSessionState],
  )

  return (
    <SessionContext.Provider value={sessionContextValue}>
      <SessionMetaContext.Provider value={sessionMetaValue}>
        <UserContext.Provider value={userSliceValue}>
          <PreferencesContext.Provider value={preferencesSliceValue}>
            <AccessibilityContext.Provider value={accessibility}>
              <NotificationPreferencesContext.Provider value={notificationPreferences}>
                <UserProfileContext.Provider value={userProfile}>
                  {children}
                </UserProfileContext.Provider>
              </NotificationPreferencesContext.Provider>
            </AccessibilityContext.Provider>
          </PreferencesContext.Provider>
        </UserContext.Provider>
      </SessionMetaContext.Provider>
    </SessionContext.Provider>
  )
}
